import {
  AlertSeverity,
  ButtonType,
  CovAlert,
  CovButton,
  CovDialog,
  type CovDialogProps,
  CovLoading,
  CovTextField,
} from "@cx-component-library/react-components";
import { Box, Stack, Typography } from "@mui/material";
import { MultiSelectChipField } from "components/input/multiSelectChipField/MultiSelectChipField";
import { PreviewImageUploader } from "components/input/previewImageUploader/PreviewImageUploader";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { CreateCollectionBody, ProjectOption } from "service/collections";
import {
  type CollectionFormState,
  emptyCollectionForm,
  isCollectionFormValid,
  toCreateCollectionBody,
} from "./create-collection-form";
import "./CreateCollectionDialog.css";

/** What the dialog hands to its host when the user presses Create. */
export interface CreateCollectionSubmission {
  body: CreateCollectionBody;
  /** Already checked as JPG/PNG ≤ 5 MB by the uploader. */
  moodboardFile?: File;
}

export interface CreateCollectionDialogProps {
  /** Every open starts from a blank form. */
  open: boolean;
  /** Close icon / backdrop / Escape — ignored while `submitting`. */
  onClose: NonNullable<CovDialogProps["onClose"]>;
  /** The Cancel button; disabled while `submitting`. */
  onCancel: () => void;
  /** Never fires for a blank name. */
  onSubmit: (submission: CreateCollectionSubmission) => void;
  /** The Projects the caller can write to, for "Add to a project". */
  projects: ProjectOption[];
  projectsLoading?: boolean;
  /** The projects could not be loaded; shown as a note, and Create stays possible. */
  projectsFailed?: boolean;
  /** A create is in flight: an overlay covers the form and the dialog can't be dismissed. */
  submitting?: boolean;
  /** Why the create failed, shown as an alert; the form keeps its values. */
  submitError?: string | null;
  /**
   * Why the chosen project was refused, shown on the project field until the
   * user changes the selection.
   */
  projectError?: string | null;
}

const RequiredMark = () => (
  <Box component="span" sx={{ color: "var(--global-red-error-red, #e50000)" }}>
    *
  </Box>
);

/**
 * "Create New Collection" (Figma 3563:44573), ported from Customer Lounge
 * (CX-3224): name (required), description, optional projects as chips and an
 * optional first moodboard image. Controlled: the projects, the in-flight state
 * and the errors come in as props; the request lives in the host.
 */
export const CreateCollectionDialog = ({
  open,
  onClose,
  onCancel,
  onSubmit,
  projects,
  projectsLoading = false,
  projectsFailed = false,
  submitting = false,
  submitError = null,
  projectError = null,
}: CreateCollectionDialogProps) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<CollectionFormState>(emptyCollectionForm);
  const [nameTouched, setNameTouched] = useState(false);
  const [moodboardFile, setMoodboardFile] = useState<File>();
  // `projectError` belongs to the selection that was submitted; hide it once that changes.
  const [submittedProjects, setSubmittedProjects] = useState<string>();

  const projectOptions = useMemo(
    () => projects.map(({ id, name }) => ({ value: id, label: name })),
    [projects],
  );
  const visibleProjectError =
    projectError && form.projectIds.join() === submittedProjects
      ? projectError
      : null;

  const previewUrl = useMemo(
    () => (moodboardFile ? URL.createObjectURL(moodboardFile) : undefined),
    [moodboardFile],
  );
  useEffect(
    () => () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    },
    [previewUrl],
  );

  useEffect(() => {
    if (open) {
      setForm(emptyCollectionForm());
      setNameTouched(false);
      setMoodboardFile(undefined);
      setSubmittedProjects(undefined);
    }
  }, [open]);

  const patchForm = (patch: Partial<CollectionFormState>) =>
    setForm((prev) => ({ ...prev, ...patch }));

  const isValid = isCollectionFormValid(form);
  const nameMissing = nameTouched && !isValid;

  const handleCreate = () => {
    if (!isValid || submitting) {
      return;
    }
    setSubmittedProjects(form.projectIds.join());
    onSubmit({ body: toCreateCollectionBody(form), moodboardFile });
  };

  const handleClose: CovDialogProps["onClose"] = (event, reason) => {
    if (!submitting) {
      onClose(event, reason);
    }
  };

  return (
    <CovDialog
      className="create-collection-dialog"
      open={open}
      onClose={handleClose}
      hasCloseButton
      title={t("src_collection_dialog_title")}
      dialogContent={
        <Stack
          sx={{
            gap: "var(--spacing32, 32px)",
            mt: "var(--spacing56, 56px)",
            pb: "var(--spacing28, 28px)",
            position: "relative",
          }}
        >
          {submitting && (
            <CovLoading
              coverParent
              spinnerText={t("src_collection_dialog_creating_overlay")}
            />
          )}
          <CovTextField
            label={
              <>
                {t("src_collection_dialog_name_label")} <RequiredMark />
              </>
            }
            placeholder={t("src_collection_dialog_name_placeholder")}
            // Figma's 40px field; CovTextField defaults to MUI "medium" (56px).
            size="small"
            fullWidth
            value={form.name}
            onChange={(event) => patchForm({ name: event.target.value })}
            onBlur={() => setNameTouched(true)}
            error={nameMissing}
            helperText={
              nameMissing ? t("src_collection_dialog_name_required") : undefined
            }
          />
          <CovTextField
            label={t("src_collection_dialog_description_label")}
            placeholder={t("src_collection_dialog_description_placeholder")}
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={form.description}
            onChange={(event) => patchForm({ description: event.target.value })}
          />
          <MultiSelectChipField
            label={t("src_collection_dialog_project_label")}
            placeholder={t("src_collection_dialog_project_placeholder")}
            options={projectOptions}
            value={form.projectIds}
            onChange={(projectIds) => patchForm({ projectIds })}
            loading={projectsLoading}
            loadingText={t("src_collection_dialog_projects_loading")}
            error={projectsFailed || !!visibleProjectError}
            helperText={
              visibleProjectError ??
              (projectsFailed
                ? t("src_collection_dialog_projects_error")
                : undefined)
            }
            noOptionsText={
              projects.length === 0
                ? t("src_collection_dialog_projects_empty")
                : t("src_collection_dialog_projects_none_left")
            }
          />
          <Stack sx={{ gap: "var(--spacing12, 12px)" }}>
            <Typography
              sx={{
                fontSize: "var(--footnote-f1-10-medium-size, 10px)",
                fontWeight: 500,
                lineHeight: 1.2,
                letterSpacing: "var(--footnote-f1-10-medium-tracking, 0)",
                color: "var(--alias-text-on-white-secondary, #262626)",
              }}
            >
              {t("src_collection_dialog_moodboard_label")}{" "}
              <Typography
                component="span"
                sx={{
                  font: "inherit",
                  color: "var(--alias-text-on-white-disable, #999)",
                }}
              >
                {t("src_collection_dialog_moodboard_hint")}
              </Typography>
            </Typography>
            <PreviewImageUploader
              testId="create-collection-moodboard-uploader"
              previewUrl={previewUrl}
              previewAlt={t("src_collection_dialog_moodboard_alt")}
              helperText={t("src_collection_dialog_moodboard_helper")}
              onFileSelected={setMoodboardFile}
              onRemove={() => setMoodboardFile(undefined)}
            />
          </Stack>
          {submitError && (
            <CovAlert severity={AlertSeverity.ERROR}>{submitError}</CovAlert>
          )}
        </Stack>
      }
      actions={
        // A fragment, not a wrapper — the DS's `.cov-dialog-actions button { flex: 1 }`
        // only splits the footer 50/50 when the buttons are its direct children.
        <>
          <CovButton
            buttonType={ButtonType.SECONDARY_OUTLINE}
            onClick={onCancel}
            disabled={submitting}
          >
            {t("src_collection_dialog_cancel")}
          </CovButton>
          <CovButton
            buttonType={ButtonType.PRIMARY}
            onClick={handleCreate}
            disabled={submitting || !isValid}
          >
            {submitting
              ? t("src_collection_dialog_creating")
              : t("src_collection_dialog_create")}
          </CovButton>
        </>
      }
    />
  );
};
